import { useEffect, useRef, useSyncExternalStore } from "react";
import { ImageUploadQueue } from "./ImageUploadQueue";
import type { ImageUploadItem, ImageUploadQueueOptions } from "./types";

export interface UseImageUploadQueueReturn {
  /** 현재 큐에 등록된 아이템 목록 */
  items: ImageUploadItem[];
  /** 파일을 큐에 추가하고 즉시 업로드 시작 */
  addFiles: (files: FileList | File[]) => void;
  /** 특정 아이템 제거 (업로드 중이면 취소) */
  removeItem: (id: string) => void;
  /** 실패한 아이템 재시도 */
  retryItem: (id: string) => void;
  /** 큐 전체 초기화 */
  clear: () => void;
  /** 하나라도 업로드 진행 중이면 true */
  isUploading: boolean;
  /** 아이템이 1개 이상이고 전부 성공이면 true */
  isAllComplete: boolean;
  /** 에러 상태 아이템이 존재하면 true */
  hasError: boolean;
}

/**
 * 이미지 업로드 큐 React 훅.
 *
 * @example 기본 사용 (컴포넌트 내부 큐)
 * ```tsx
 * const upload = useImageUploadQueue({
 *   getPresignedUrl: async (file) => {
 *     const { data } = await apiClient.post("/upload/presigned", { ... });
 *     return { uploadUrl: data.uploadUrl, fileUrl: data.fileUrl };
 *   },
 *   onSuccess: (items) => console.log("All done!", items),
 * });
 * ```
 *
 * @example 외부 큐 (라우트 이동 시에도 업로드 유지)
 * ```tsx
 * // 모듈 스코프 또는 부모 컴포넌트
 * const queue = createImageUploadQueue({ getPresignedUrl: ... });
 *
 * // 자식 컴포넌트
 * const upload = useImageUploadQueue(queue);
 * ```
 */
export function useImageUploadQueue(
  optionsOrQueue: ImageUploadQueueOptions | ImageUploadQueue,
): UseImageUploadQueueReturn {
  const isExternal = optionsOrQueue instanceof ImageUploadQueue;

  const internalRef = useRef<ImageUploadQueue | null>(null);
  if (!isExternal && !internalRef.current) {
    internalRef.current = new ImageUploadQueue(optionsOrQueue);
  }

  const queue = isExternal ? optionsOrQueue : internalRef.current!;

  const optionsRef = useRef(!isExternal ? optionsOrQueue : null);
  if (!isExternal) optionsRef.current = optionsOrQueue as ImageUploadQueueOptions;

  useEffect(() => {
    if (!isExternal && optionsRef.current) {
      queue.updateOptions(optionsRef.current);
    }
  });

  useEffect(() => {
    return () => {
      if (!isExternal) {
        internalRef.current?.destroy();
        internalRef.current = null;
      }
    };
  }, [isExternal]);

  const items = useSyncExternalStore(queue.subscribe, queue.getSnapshot, queue.getSnapshot);

  return {
    items,
    addFiles: queue.addFiles,
    removeItem: queue.removeItem,
    retryItem: queue.retryItem,
    clear: queue.clear,
    isUploading: items.some((i) => i.status === "uploading" || i.status === "pending"),
    isAllComplete: items.length > 0 && items.every((i) => i.status === "success"),
    hasError: items.some((i) => i.status === "error"),
  };
}
